import { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import useAuth from '../hooks/useAuth';

export enum EnumMode {
  Client = 'client',
  Supplier = 'supplier',
}

interface IModeContextValue {
  mode: EnumMode | null;
  changeMode?: (newMode: EnumMode) => void;
}

export const ModeContext = createContext<IModeContextValue>({
  mode: null,
  changeMode: undefined,
});

interface IModeProviderProps {
  children: ReactNode;
}

const getStoredModeData = () => {
  const modeData = localStorage.getItem('mode');
  const modeDataParsed = modeData ? JSON.parse(modeData) : null;

  if (
    !modeDataParsed ||
    !modeDataParsed.mode ||
    ![EnumMode.Client, EnumMode.Supplier].includes(modeDataParsed.mode)
  ) {
    return { mode: null, id: null };
  }

  return modeDataParsed;
};

export const ModeProvider = ({ children }: IModeProviderProps) => {
  const {
    authData: { userData },
  } = useAuth();

  const storedModeData = getStoredModeData();
  const [mode, setMode] = useState<EnumMode | null>(storedModeData.mode || EnumMode.Client); // default mode

  const changeMode = useCallback(
    (newMode: EnumMode) => {
      setMode(newMode);
      localStorage.setItem('mode', JSON.stringify({ mode: newMode, id: userData?.id ?? null }));
    },
    [setMode, userData],
  );

  const contextValue = useMemo(
    () => ({
      mode,
      changeMode,
    }),
    [mode, changeMode],
  );

  return <ModeContext.Provider value={contextValue}>{children}</ModeContext.Provider>;
};
